import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Cluster, Stack } from 'component-library-next'

import { CasesListFilterInput } from '@app/graphql/types'
import { useIntl } from '@app/intl/Intl'
import { ProductSelect } from '@app/lib/components/ProductSelect/ProductSelect'
import { InputField } from '@app/lib/fields/InputField/InputField'
import { Button } from '@app/lib/inputs/Button/Button'
import { InputBox } from '@app/lib/layout/InputBox/InputBox'

export type AuditReportsFilterProps = {
  values: CasesListFilterInput
  onSubmit: (values: CasesListFilterInput) => void
  onReset: () => void
}

type FormData = {
  approvedAfter: string
  approvedBefore: string
  productName: string
}

export const AuditReportsFilterForm = ({ onSubmit, onReset, values }: AuditReportsFilterProps) => {
  const { formatMessage } = useIntl()

  const methods = useForm<FormData>({
    defaultValues: {
      approvedAfter: values.latestQuestionApprovedAt?.gte || '',
      approvedBefore: values.latestQuestionApprovedAt?.lte || '',
      productName: values.productName?.equals || ''
    }
  })

  const handleSubmit = (values: FormData) => {
    onSubmit({
      ...(values.approvedAfter || values.approvedBefore
        ? {
            latestQuestionApprovedAt: {
              ...(values.approvedAfter && {
                gte: values.approvedAfter
              }),
              ...(values.approvedBefore && {
                lte: values.approvedBefore
              })
            }
          }
        : { latestQuestionApprovedAt: undefined }),
      ...(values.productName
        ? {
            productName: { equals: values.productName }
          }
        : { productName: undefined })
    })
  }

  const handleReset = () => {
    methods.reset({})
    onReset()
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Stack>
          <InputField
            name='approvedAfter'
            label={formatMessage({ id: 'Lawyer.AuditReports.Filter.ApprovedAfter' })}
            render={({ inputProps }) => {
              return <InputBox control={<input type={'date'} {...inputProps} />} />
            }}
          />
          <InputField
            name='approvedBefore'
            label={formatMessage({ id: 'Lawyer.AuditReports.Filter.ApprovedBefore' })}
            render={({ inputProps }) => {
              return <InputBox control={<input {...inputProps} type={'date'} />} />
            }}
          />
          <InputField
            name='productName'
            label={formatMessage({ id: 'Lawyer.AuditReports.Filter.ProductName' })}
            render={({ inputProps }) => {
              return <ProductSelect {...inputProps} />
            }}
          />
          <Cluster justification={Cluster.justification.right} gap={Cluster.gap.small}>
            <Button variant='outlined' color='neutral' type='reset' onClick={handleReset}>
              {formatMessage({ id: 'Lawyer.AuditReports.Filter.Btn.Reset' })}
            </Button>
            <Button variant='solid' color='primary' type='submit'>
              {formatMessage({ id: 'Lawyer.AuditReports.Filter.Btn.Filter' })}
            </Button>
          </Cluster>
        </Stack>
      </form>
    </FormProvider>
  )
}
